import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createBlock as _createBlock, unref as _unref, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  "aria-labelledby": "products-heading",
  class: "sticky top-0 pt-10 h-fit grow lg:w-3/4 bg-gray-100 px-2 ld:px-6 lg:border-r border-gray-200"
}
const _hoisted_2 = {
  id: "products-heading",
  class: "sr-only"
}
const _hoisted_3 = {
  key: 0,
  class: "grid grid-cols-2 gap-6 lg:grid-cols-4"
}
const _hoisted_4 = {
  key: 1,
  class: "flex justify-center items-center min-h-[300px]"
}
const _hoisted_5 = { class: "text-center" }
const _hoisted_6 = { class: "text-red-600 text-base mb-3" }
const _hoisted_7 = {
  key: 2,
  class: "pb-20"
}
const _hoisted_8 = {
  key: 1,
  class: "grid gap-2 grid-cols-2 xl:grid-cols-4 xl:gap-6 gap-y-2"
}
const _hoisted_9 = {
  key: 0,
  class: "col-span-2 row-span-2"
}
const _hoisted_10 = {
  key: 1,
  class: "col-span-2 row-span-2 flex"
}
const _hoisted_11 = {
  key: 2,
  class: "mt-10 flex justify-center uppercase"
}
const _hoisted_12 = ["disabled"]
const _hoisted_13 = { class: "uppercase" }
const _hoisted_14 = {
  key: 3,
  class: "mt-12 border-t border-gray-300 pt-12"
}
const _hoisted_15 = { class: "text-base font-light text-center mb-8 uppercase tracking-widest text-gray-500" }
const _hoisted_16 = { class: "grid gap-6 grid-cols-2 xl:grid-cols-4 gap-y-6" }
const _hoisted_17 = {
  key: 0,
  class: "mt-10 flex justify-center uppercase"
}
const _hoisted_18 = ["disabled"]
const _hoisted_19 = { class: "uppercase" }
const _hoisted_20 = {
  key: 4,
  class: "mt-12 border-t border-gray-300 pt-12"
}
const _hoisted_21 = { class: "flex flex-col items-center" }
const _hoisted_22 = { class: "text-base font-light text-center mb-4 uppercase tracking-widest text-gray-500" }
const _hoisted_23 = {
  key: 0,
  class: "grid gap-6 grid-cols-2 xl:grid-cols-4 gap-y-6"
}

import { ref, watch, computed } from 'vue'
import { useI18n } from "vue-i18n";
import { filterListType } from "@/assets/ts/types/filters";
import CatalogItem from "@/components/catalog/catalogItem.vue";
import CatalogBanner from "@/components/catalog/catalogBanner.vue";
import CatalogItemSkeleton from "@/components/catalog/catalogItemSkeleton.vue";
import CatalogResultEmpty from "@/components/catalog/catalogResultEmpty.vue";
import {Config} from "@/assets/ts/config";

interface Product {
  set_id: string;
  hash?: string;
  price: number;
  price_gold: number;
  price_platinum: number;
  metal: string;
  stones: string;
  image: {
    filename: string
    filename_layer: string
    positions: {
      filename: string
      filename_layer: string
    }[]
  };
  order_idx?: number;
  link: string
}

interface CatalogResponse {
  request: {
    max: number;
    [key: string]: any;
  };
  filter: filterListType;
  images: string[];
  response: Product[];
}

interface IndividualItemResponse {
  request: {
    catalog_id: string;
    idx: number;
    max: number;
    parameter: object;
  };
  response: Product & {
    order_idx: number;
    status: string;
  };
}

interface MultipleItemsResponse {
  request: {
    catalog_id: string;
    idx: number[];
    max: number;
    parameter: object;
  };
  response: (Product & {
    order_idx: number;
    status: string;
  })[];
}

const pageStr = "components.catalog"

const maxLoadMoreItems = 16;
const maxConcurrentRequests = 16;
const requestTimeoutMs = 10000;
// Number of idxs fetched per HTTP request. When set to 1, items are fetched one by one
// via fetchIndividualItem (the original process). When > 1, idxs are grouped and fetched
// together via fetchMultipleItems, which hits a single endpoint returning a list of items.
const idxBatchSize = 4;

// Aborts all in-flight requests belonging to the previous "generation" whenever
// a brand new fetchProducts() call supersedes them (e.g. filters changed mid-flight).

export default /*@__PURE__*/_defineComponent({
  __name: 'catalogResult',
  props: {
    catalogBrand: {},
    catalogItemType: {},
    activeFilters: {},
    country: {},
    language: {},
    fixImagePosition: {},
    hash: {}
  },
  emits: ["update:products", "update:failedProducts", "update:filters", "update:possibleImagePositions", "update:loading", "update:error", "update-filter"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const { t } = useI18n();
const products = ref<Product[]>([]);
const failedProducts = ref<Product[]>([]);
watch(failedProducts, (val) => {
  emit('update:failedProducts', val);
});
const filters = ref<filterListType>({});
const possibleImagePositions = ref<string[]>([]);
const loading = ref(true);
const loadingMore = ref(false);
const isLoadingMore = ref(false);
const error = ref<string | null>(null);
const usedHashes = new Set<string>();
const currentIndex = ref(0);
const totalMaxItems = ref(0);
const bufferedItems = ref<Product[]>([]);
const lastFetchedParams = ref('');
const itemsAddedInCurrentBatch = ref(0);

const failedProductsExpanded = ref(false);
const labProducts = ref<Product[]>([]);
const labTotalMaxItems = ref(0);
const labCurrentIndex = ref(0);
const labBufferedItems = ref<Product[]>([]);
const labLoadingMore = ref(false);
const isLoadingMoreLab = ref(false);
const labItemsAddedInCurrentBatch = ref(0);

let activeAbortController = new AbortController();
// De-duplicates concurrent/duplicate fetchIndividualItem calls for the same idx/filters combo.
const individualItemCache = new Map<string, Promise<IndividualItemResponse | null>>();
// De-duplicates concurrent/duplicate fetchMultipleItems calls for the same idxs/filters combo.
const multipleItemsCache = new Map<string, Promise<MultipleItemsResponse['response'] | null>>();

const isPriceFilterActive = computed(() => {
  return !!props.activeFilters.price
})

const isMetalFilterActive = computed(() => {
  return !!props.activeFilters.metal
})

const isWeddingCatalog = computed(() => {
  return props.catalogItemType.includes('wedding')
})

// For wedding ring catalogs, the "min-price" sort is always applied
// automatically as long as no price or metal filter is active.
const isMinPriceAllowed = computed(() => {
  return isWeddingCatalog.value && !isPriceFilterActive.value && !isMetalFilterActive.value
})

const showLabProducts = computed(() => {
  return isPriceFilterActive.value && props.activeFilters.stone_type !== 'lab'
})

const sortProducts = (products: Product[]) => {
  return products.sort((a, b) => {
    if (isPriceFilterActive.value) {
      return (a.price ?? 0) - (b.price ?? 0);
    }
    return (a.order_idx ?? 0) - (b.order_idx ?? 0);
  });
};

const getQueryParams = () => {
  const queryParams = new URLSearchParams();
  Object.entries(props.activeFilters)
      .filter(([key, value]) => !(key === 'stone_type' && value === 'earth'))
      .forEach(([key, value]) => queryParams.append(key, value));

  return queryParams;
}

const fetchIndividualItem = async (idx: number, baseQueryParams: URLSearchParams, stoneTypeOverride?: string, signal?: AbortSignal): Promise<IndividualItemResponse | null> => {
  if (signal?.aborted) {
    return null;
  }

  const cacheKey = `${idx}-${stoneTypeOverride ?? ''}-${baseQueryParams.toString()}`;
  const cached = individualItemCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const queryParams = new URLSearchParams(baseQueryParams);
  if (stoneTypeOverride) {
    queryParams.set('stone_type', stoneTypeOverride);
  }
  const url = `${process.env.VUE_APP_API_HOST}/catalog/v2/${props.catalogBrand}/${props.country}/${props.language}/${props.catalogItemType}/${idx}?${queryParams.toString()}`;

  const requestPromise = (async (): Promise<IndividualItemResponse | null> => {
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), requestTimeoutMs);
    const onExternalAbort = () => timeoutController.abort();
    signal?.addEventListener('abort', onExternalAbort);

    try {
      const response = await fetch(url, { signal: timeoutController.signal });
      if (!response.ok) {
        return null;
      }
      return await response.json();
    } catch (e) {
      if (!signal?.aborted) {
        console.error(`Failed to fetch item ${idx}`, e);
      }
      return null;
    } finally {
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', onExternalAbort);
    }
  })();

  individualItemCache.set(cacheKey, requestPromise);
  requestPromise.then((result) => {
    // Only cache successful lookups; allow failed/aborted/timed-out requests to be retried later.
    if (result === null) {
      individualItemCache.delete(cacheKey);
    }
  });

  return requestPromise;
};

// Fetches multiple idxs in a single request. The idxs are joined by "-" in the URL and the
// response is a list of items (instead of the single object returned by fetchIndividualItem).
const fetchMultipleItems = async (idxs: number[], baseQueryParams: URLSearchParams, stoneTypeOverride?: string, signal?: AbortSignal): Promise<MultipleItemsResponse['response'] | null> => {
  if (signal?.aborted || idxs.length === 0) {
    return null;
  }

  const idxKey = idxs.join('-');
  const cacheKey = `${idxKey}-${stoneTypeOverride ?? ''}-${baseQueryParams.toString()}`;
  const cached = multipleItemsCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const queryParams = new URLSearchParams(baseQueryParams);
  if (stoneTypeOverride) {
    queryParams.set('stone_type', stoneTypeOverride);
  }
  const url = `${process.env.VUE_APP_API_HOST}/catalog/v2/${props.catalogBrand}/${props.country}/${props.language}/${props.catalogItemType}/${idxKey}?${queryParams.toString()}`;

  const requestPromise = (async (): Promise<MultipleItemsResponse[] | null> => {
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), requestTimeoutMs);
    const onExternalAbort = () => timeoutController.abort();
    signal?.addEventListener('abort', onExternalAbort);

    try {
      const response = await fetch(url, { signal: timeoutController.signal });
      if (!response.ok) {
        return null;
      }
      const data: MultipleItemsResponse[] = await response.json();

      return data.map(item => item.response);
    } catch (e) {
      if (!signal?.aborted) {
        console.error(`Failed to fetch items ${idxKey}`, e);
      }
      return null;
    } finally {
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', onExternalAbort);
    }
  })();

  multipleItemsCache.set(cacheKey, requestPromise);
  requestPromise.then((result) => {
    // Only cache successful lookups; allow failed/aborted/timed-out requests to be retried later.
    if (result === null) {
      multipleItemsCache.delete(cacheKey);
    }
  });

  return requestPromise;
};

// Fetches a chunk of idxs, transparently using fetchIndividualItem when the chunk holds a single
// idx (idxBatchSize === 1, the original process) or fetchMultipleItems for larger chunks.
const fetchIndicesChunk = async (idxs: number[], baseQueryParams: URLSearchParams, stoneTypeOverride?: string, signal?: AbortSignal): Promise<MultipleItemsResponse['response']> => {
  if (idxs.length === 1) {
    const data = await fetchIndividualItem(idxs[0], baseQueryParams, stoneTypeOverride, signal);
    return data && data.response ? [data.response] : [];
  }
  const items = await fetchMultipleItems(idxs, baseQueryParams, stoneTypeOverride, signal);
  return items ?? [];
};

const fetchItemsBatch = async (targetCount: number, context: 'main' | 'lab' = 'main') => {
  const isLab = context === 'lab';
  const productsRef = isLab ? labProducts : products;
  const currentIndexRef = isLab ? labCurrentIndex : currentIndex;
  const totalMaxItemsRef = isLab ? labTotalMaxItems : totalMaxItems;
  const bufferedItemsRef = isLab ? labBufferedItems : bufferedItems;
  const isLoadingMoreRef = isLab ? isLoadingMoreLab : isLoadingMore;
  const loadingMoreRef = isLab ? labLoadingMore : loadingMore;
  const itemsAddedInCurrentBatchRef = isLab ? labItemsAddedInCurrentBatch : itemsAddedInCurrentBatch;
  const stoneTypeOverride = isLab ? 'lab' : undefined;
  const baseQueryParams = getQueryParams();
  const signal = activeAbortController.signal;

  itemsAddedInCurrentBatchRef.value = 0;

  try {
    const mainOrderIndices = new Set(products.value.map(p => p.order_idx));

    const flushItems = (itemsToFlush: Product[]) => {
      if (itemsToFlush.length === 0) return;

      let itemsToActuallyAdd: Product[] = [];
      let itemsToBuffer: Product[] = [];

      for (const item of itemsToFlush) {
        if (itemsAddedInCurrentBatchRef.value + itemsToActuallyAdd.length < targetCount) {
          itemsToActuallyAdd.push(item);
        } else {
          itemsToBuffer.push(item);
        }
      }

      if (itemsToActuallyAdd.length > 0) {
        const existingIds = new Set(productsRef.value.map(p => p.set_id));
        const uniqueItems = itemsToActuallyAdd.filter(item => !existingIds.has(item.set_id));

        if (uniqueItems.length > 0) {
          productsRef.value = sortProducts([...productsRef.value, ...uniqueItems]);
          itemsAddedInCurrentBatchRef.value += uniqueItems.length;

          if (!isLab) {
            if (loading.value) {
              loading.value = false;
              emit('update:loading', false);
            }
            emit('update:products', products.value);

            const newlyAddedIndices = new Set(uniqueItems.map(p => p.order_idx));
            labProducts.value = labProducts.value.filter(p => !newlyAddedIndices.has(p.order_idx));
            labBufferedItems.value = labBufferedItems.value.filter(p => !newlyAddedIndices.has(p.order_idx));
          }
        }
      }

      if (itemsToBuffer.length > 0) {
        bufferedItemsRef.value.push(...itemsToBuffer);
      }
    };

    // Pull from buffer first
    let itemsFromBuffer: Product[] = [];
    while (bufferedItemsRef.value.length > 0 && itemsFromBuffer.length < targetCount) {
      const item = bufferedItemsRef.value.shift()!;
      if (isLab) {
        if (!mainOrderIndices.has(item.order_idx)) {
          itemsFromBuffer.push(item);
          failedProducts.value = failedProducts.value.filter(p => p.order_idx !== item.order_idx);
        }
      } else {
        itemsFromBuffer.push(item);
        labProducts.value = labProducts.value.filter(p => p.order_idx !== item.order_idx);
        labBufferedItems.value = labBufferedItems.value.filter(p => p.order_idx !== item.order_idx);
      }
    }
    flushItems(itemsFromBuffer);

    if (itemsAddedInCurrentBatchRef.value < targetCount && currentIndexRef.value < totalMaxItemsRef.value) {
      isLoadingMoreRef.value = true;
      let successfulInFetch = 0;
      const neededFromFetch = targetCount - itemsAddedInCurrentBatchRef.value;
      const activeRequests = new Set<Promise<void>>();
      const fetchedItems: Product[] = [];
      const fetchedFailedItems: Product[] = [];
      let lastFlushedCount = 0;

      while (successfulInFetch < neededFromFetch && currentIndexRef.value < totalMaxItemsRef.value) {
        const remainingNeeded = neededFromFetch - successfulInFetch;
        const maxActiveRequests = Math.min(maxConcurrentRequests, Math.ceil(remainingNeeded / idxBatchSize));
        if (activeRequests.size < maxActiveRequests && currentIndexRef.value < totalMaxItemsRef.value) {
          const chunkIdxs: number[] = [];
          while (chunkIdxs.length < idxBatchSize && currentIndexRef.value < totalMaxItemsRef.value) {
            chunkIdxs.push(currentIndexRef.value++);
          }
          const promise = (async () => {
            const items = await fetchIndicesChunk(chunkIdxs, baseQueryParams, stoneTypeOverride, signal);
            for (const responseItem of items) {
              const item = {
                ...responseItem,
                order_idx: responseItem.order_idx
              };
              if (responseItem.status !== 'failed') {
                if (!item.hash || !usedHashes.has(item.hash)) {
                  if (item.hash) usedHashes.add(item.hash);

                  if (isLab) {
                    if (!mainOrderIndices.has(item.order_idx)) {
                      fetchedItems.push(item);
                      successfulInFetch++;
                      failedProducts.value = failedProducts.value.filter(p => p.order_idx !== item.order_idx);
                    }
                  } else {
                    fetchedItems.push(item);
                    successfulInFetch++;
                    labProducts.value = labProducts.value.filter(p => p.order_idx !== item.order_idx);
                    labBufferedItems.value = labBufferedItems.value.filter(p => p.order_idx !== item.order_idx);
                  }
                }
              } else {
                fetchedFailedItems.push(item);
              }
            }
            activeRequests.delete(promise);
          })();
          activeRequests.add(promise);
        } else if (activeRequests.size > 0) {
          await Promise.race(activeRequests);

          const newlyFetchedCount = fetchedItems.length - lastFlushedCount;
          if (newlyFetchedCount >= 4) {
            const itemsToFlush = sortProducts(fetchedItems.slice(lastFlushedCount));
            flushItems(itemsToFlush);
            lastFlushedCount = fetchedItems.length;
          }
        } else {
          break;
        }
      }

      await Promise.all(activeRequests);

      if (lastFlushedCount < fetchedItems.length) {
        const itemsToFlush = sortProducts(fetchedItems.slice(lastFlushedCount));
        flushItems(itemsToFlush);
      }

      if (!isLab) {
        failedProducts.value = sortProducts([...failedProducts.value, ...fetchedFailedItems]);
      }
    }

    const finalMainIndices = new Set(products.value.map(p => p.order_idx));
    labProducts.value = labProducts.value.filter(p => !finalMainIndices.has(p.order_idx));
    labBufferedItems.value = labBufferedItems.value.filter(p => !finalMainIndices.has(p.order_idx));

    loadingMoreRef.value = currentIndexRef.value < totalMaxItemsRef.value || bufferedItemsRef.value.length > 0;
  } catch (e) {
    console.error(e)
    if (!isLab) {
      error.value = t(`${pageStr}.load_failed`);
      emit('update:error', error.value);
    }
  } finally {
    isLoadingMoreRef.value = false;
    if (!isLab) {
      emit('update:products', products.value);
    }
  }
};

const fetchLabProductsData = async () => {
  const queryParams = getQueryParams();
  queryParams.set('stone_type', 'lab');
  const signal = activeAbortController.signal;

  try {
    labProducts.value = [];
    labCurrentIndex.value = 0;
    labBufferedItems.value = [];
    labTotalMaxItems.value = 0;

    const url = `${process.env.VUE_APP_API_HOST}/catalog/v2/${props.catalogBrand}/${props.country}/${props.language}/${props.catalogItemType}?${queryParams.toString()}`;

    const response = await fetch(url, { signal });
    if (!response.ok) {
      return;
    }
    const data: CatalogResponse = await response.json();

    labTotalMaxItems.value = data.request.max;
    await fetchItemsBatch(maxLoadMoreItems, 'lab');
  } catch (e) {
    if (!signal.aborted) {
      console.error("Failed to fetch lab products", e);
    }
  }
}

const fetchProducts = async () => {
  const queryParams = getQueryParams();
  const currentParams = `${props.catalogItemType}?${queryParams.toString()}`;

  if (currentParams === lastFetchedParams.value) return;
  lastFetchedParams.value = currentParams;

  // Cancel any requests still in flight from a previous, now-superseded generation.
  activeAbortController.abort();
  activeAbortController = new AbortController();
  const signal = activeAbortController.signal;
  individualItemCache.clear();
  multipleItemsCache.clear();

  try {
    loading.value = true;
    emit('update:loading', true);
    error.value = null;
    emit('update:error', null);

    products.value = [];
    failedProducts.value = [];
    usedHashes.clear();
    currentIndex.value = 0;
    totalMaxItems.value = 0;
    bufferedItems.value = [];
    failedProductsExpanded.value = false;

    labProducts.value = [];
    labCurrentIndex.value = 0;
    labBufferedItems.value = [];
    labTotalMaxItems.value = 0;
    loadingMore.value = false;
    labLoadingMore.value = false;
    itemsAddedInCurrentBatch.value = 0;
    labItemsAddedInCurrentBatch.value = 0;

    emit('update:products', []);

    const url = `${process.env.VUE_APP_API_HOST}/catalog/v2/${props.catalogBrand}/${props.country}/${props.language}/${props.catalogItemType}?${queryParams.toString()}`;

    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }
    const data: CatalogResponse = await response.json();

    totalMaxItems.value = data.request.max;
    filters.value = data.filter;
    possibleImagePositions.value = data.images;

    emit('update:filters', filters.value);
    emit('update:possibleImagePositions', possibleImagePositions.value);

    await fetchItemsBatch(maxLoadMoreItems);

    if (showLabProducts.value) {
      await fetchLabProductsData();
    } else {
      labProducts.value = [];
      labTotalMaxItems.value = 0;
    }

  } catch (e) {
    if (signal.aborted) {
      return;
    }
    console.error(e)
    lastFetchedParams.value = '';
    error.value = t(`${pageStr}.load_failed`);
    emit('update:error', error.value);
  } finally {
    if (!signal.aborted) {
      loading.value = false;
      emit('update:loading', false);
    }
  }

  // After the main catalog is loaded, apply initial values for filters (if provided by API)
  const initialFilters = Object.entries(filters.value)
      .map(([key, f]) => [key, (f as any)?.init as string | undefined] as const)
      .filter(([key, initVal]) => typeof initVal === 'string' && initVal !== '' && (!props.activeFilters[key] || props.activeFilters[key] === ''));
  for (const [key, initVal] of initialFilters) {
    console.log(`Applying initial filter ${key}=${initVal}`);
    emit('update-filter', key, initVal as string);
  }


};

const loadMore = () => {
  fetchItemsBatch(maxLoadMoreItems);
};

const loadMoreLab = () => {
  fetchItemsBatch(maxLoadMoreItems, 'lab');
};

const addMarketingBoxLeft = (itemIdx: number) => {
  return itemIdx == 4 || (itemIdx - 4) % 16 == 0
}

const addMarketingBoxRight = (itemIdx: number) => {
  return itemIdx == 13 || (itemIdx - 13) % 16 == 0
}

// Watchers to trigger fetch
watch(() => [props.catalogItemType, props.activeFilters], () => {
  fetchProducts();
}, { deep: true, immediate: true });

const isMinPrice = computed(() => {
  // is price sort min-price (either explicitly selected or auto-applied for wedding rings)
  return props.activeFilters.price_sort === 'min-price' && isMinPriceAllowed.value
})


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("h2", _hoisted_2, _toDisplayString(_ctx.$t(`${pageStr}.products_sr`)), 1),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(maxLoadMoreItems, (i) => {
            return _createVNode(CatalogItemSkeleton, { key: i })
          }), 64))
        ]))
      : (error.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, _toDisplayString(error.value), 1),
              _createElementVNode("button", {
                class: "px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (fetchProducts()))
              }, _toDisplayString(_ctx.$t(`${pageStr}.try_again`)), 1)
            ])
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_7, [
            (products.value.length === 0)
              ? (_openBlock(), _createBlock(CatalogResultEmpty, {
                  key: 0,
                  "active-filters": __props.activeFilters,
                  "onUpdate:filter": _cache[1] || (_cache[1] = (name, value) => _ctx.$emit('update-filter', name, value))
                }, null, 8, ["active-filters"]))
              : _createCommentVNode("", true),
            (products.value.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(products.value, (item, index) => {
                    return (_openBlock(), _createElementBlock(_Fragment, {
                      key: item.set_id
                    }, [
                      (addMarketingBoxLeft(index))
                        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                            _createVNode(CatalogBanner, {
                              class: "col-span-2",
                              index: index,
                              item: item,
                              "catalog-banners": _unref(Config).getCatalogBanners()
                            }, null, 8, ["index", "item", "catalog-banners"])
                          ]))
                        : _createCommentVNode("", true),
                      _createVNode(CatalogItem, {
                        product: item,
                        index: index,
                        hash: __props.hash,
                        "catalog-item-type": __props.catalogItemType,
                        "fix-image-position": __props.fixImagePosition,
                        "is-min-Price": isMinPrice.value
                      }, null, 8, ["product", "index", "hash", "catalog-item-type", "fix-image-position", "is-min-Price"]),
                      (addMarketingBoxRight(index))
                        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                            _createVNode(CatalogBanner, {
                              class: "col-span-2",
                              index: index,
                              item: item,
                              "catalog-banners": _unref(Config).getCatalogBanners()
                            }, null, 8, ["index", "item", "catalog-banners"])
                          ]))
                        : _createCommentVNode("", true)
                    ], 64))
                  }), 128)),
                  (isLoadingMore.value)
                    ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList((maxLoadMoreItems - itemsAddedInCurrentBatch.value), (i) => {
                        return (_openBlock(), _createBlock(CatalogItemSkeleton, {
                          key: 'skeleton-' + i
                        }))
                      }), 128))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true),
            (loadingMore.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                  _createElementVNode("button", {
                    class: "cursor-pointer px-20 py-3 bg-white border border-brand-main text-brand-text hover:bg-brand-main hover:text-white text-base tracking-widest disabled:opacity-60 disabled:cursor-not-allowed",
                    disabled: isLoadingMore.value,
                    onClick: loadMore
                  }, [
                    _createElementVNode("span", _hoisted_13, _toDisplayString(_ctx.$t(`${pageStr}.load_more`)), 1)
                  ], 8, _hoisted_12)
                ]))
              : _createCommentVNode("", true),
            (labProducts.value.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                  _createElementVNode("h3", _hoisted_15, _toDisplayString(_ctx.$t(`${pageStr}.further_possibilities_lab`)), 1),
                  _createElementVNode("div", _hoisted_16, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(labProducts.value, (item) => {
                      return (_openBlock(), _createBlock(CatalogItem, {
                        key: 'lab-' + item.set_id,
                        product: item,
                        hash: __props.hash,
                        "catalog-item-type": __props.catalogItemType,
                        "fix-image-position": __props.fixImagePosition
                      }, null, 8, ["product", "hash", "catalog-item-type", "fix-image-position"]))
                    }), 128)),
                    (isLoadingMoreLab.value)
                      ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList((maxLoadMoreItems - labItemsAddedInCurrentBatch.value), (i) => {
                          return (_openBlock(), _createBlock(CatalogItemSkeleton, {
                            key: 'lab-skeleton-' + i
                          }))
                        }), 128))
                      : _createCommentVNode("", true)
                  ]),
                  (labLoadingMore.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                        _createElementVNode("button", {
                          class: "cursor-pointer px-20 py-3 bg-white border border-brand-main text-brand-text hover:bg-brand-main hover:text-white text-base tracking-widest disabled:opacity-60 disabled:cursor-not-allowed",
                          disabled: isLoadingMoreLab.value,
                          onClick: loadMoreLab
                        }, [
                          _createElementVNode("span", _hoisted_19, _toDisplayString(_ctx.$t(`${pageStr}.load_more`)), 1)
                        ], 8, _hoisted_18)
                      ]))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true),
            (failedProducts.value.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                  _createElementVNode("div", _hoisted_21, [
                    _createElementVNode("h3", _hoisted_22, _toDisplayString(_ctx.$t(`${pageStr}.failed_products_title`)), 1),
                    _createElementVNode("button", {
                      onClick: _cache[2] || (_cache[2] = ($event: any) => (failedProductsExpanded.value = !failedProductsExpanded.value)),
                      class: "mb-8 text-xs uppercase tracking-[0.2em] text-gray-400 hover:text-gray-900 transition-colors flex items-center gap-2"
                    }, [
                      _createTextVNode(_toDisplayString(failedProductsExpanded.value ? _ctx.$t(`${pageStr}.hide_failed_products`) : _ctx.$t(`${pageStr}.show_failed_products`)) + " ", 1),
                      (_openBlock(), _createElementBlock("svg", {
                        xmlns: "http://www.w3.org/2000/svg",
                        class: _normalizeClass(["h-4 w-4 transition-transform", {'rotate-180': failedProductsExpanded.value}]),
                        fill: "none",
                        viewBox: "0 0 24 24",
                        stroke: "currentColor"
                      }, [...(_cache[3] || (_cache[3] = [
                        _createElementVNode("path", {
                          "stroke-linecap": "round",
                          "stroke-linejoin": "round",
                          "stroke-width": "2",
                          d: "M19 9l-7 7-7-7"
                        }, null, -1)
                      ]))], 2))
                    ])
                  ]),
                  (failedProductsExpanded.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(failedProducts.value, (item) => {
                          return (_openBlock(), _createBlock(CatalogItem, {
                            key: 'failed-' + item.set_id,
                            product: item,
                            "catalog-item-type": __props.catalogItemType,
                            "fix-image-position": __props.fixImagePosition
                          }, null, 8, ["product", "catalog-item-type", "fix-image-position"]))
                        }), 128))
                      ]))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true)
          ]))
  ]))
}
}

})